import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MenuIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import VisibilityOnIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { yellow } from '@mui/material/colors';

import { Level } from '@paragon/contract-library';

import { handleStandardSceneItemRightClick } from '../../../../store/actions/sceneInteractionActions';
import { selectItemByGuid } from '../../../../store/actions/selectActions';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { LevelSecondaryText } from './LevelSecondaryText';

interface LevelListItemProps {
  level: Level;
  displayName: string;
  active: boolean;
  visible: boolean;
  toggleVisibility: () => void;
  onContextMenuDidOpen?: () => void;
}

export function LevelListItem(props: LevelListItemProps) {
  const { active, displayName, level, onContextMenuDidOpen, toggleVisibility, visible } = props;

  const dispatch = useAppDispatch();

  const clientContext = useAppSelector((state) => state.application.openContext);
  const inProjectContext = clientContext?.type === 'project';

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: level.guid });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  async function handleMenuRequested(event: React.MouseEvent) {
    event.stopPropagation();

    const menuDidOpen = await dispatch(
      handleStandardSceneItemRightClick(event, {
        type: 'level',
        guid: level.guid,
        origin: 'dom',
      }),
    );

    if (menuDidOpen && onContextMenuDidOpen) {
      onContextMenuDidOpen();
    }
  }

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      secondaryAction={
        inProjectContext && (
          <IconButton onClick={handleMenuRequested}>
            <MenuIcon />
          </IconButton>
        )
      }
      disablePadding
    >
      <ListItemButton
        dense
        selected={active}
        onClick={() => {
          dispatch(selectItemByGuid(level.guid, 'level'));
        }}
        onContextMenu={async (event) => {
          handleMenuRequested(event);
        }}
      >
        <ListItemIcon>
          <Tooltip
            title={
              active
                ? 'This is the active level.'
                : visible
                  ? 'This level is visible. Click to hide it.'
                  : 'This level is hidden. Click to show it.'
            }
            placement="right"
          >
            <IconButton
              sx={{ color: (theme) => (active ? yellow[700] : visible ? undefined : theme.palette.text.disabled) }}
              onClick={(event) => {
                event.stopPropagation();
                toggleVisibility();
              }}
            >
              {active ? <StarIcon /> : visible ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
        </ListItemIcon>

        <ListItemText primary={displayName} secondary={<LevelSecondaryText level={level} />} />
      </ListItemButton>
    </ListItem>
  );
}
